// How a chart animates a data change, for callers where a data change is a NAVIGATION rather than a
// refresh: the dashboard's hotspot cards, where clicking a bar replaces the rows with its breakdown.
//
// Opt-in, because it is a judgement about meaning rather than a better default. The deep-dive modal
// and the waste sidebar share this chart and keep ECharts' own timings.
import type { ChartSettingsType } from "@/features/Charts/types/ChartType";

type MotionSettings = Pick<ChartSettingsType, "motion">;

// duration-normal / duration-fast / ease-standard, as literals because ECharts cannot read a CSS
// variable. Keep in step with the tokens in src/style.css.
const ROWS_IN_MS = 300;
// The handover half of a navigation: the rows being replaced shrink back to the axis. Deliberately
// shorter than the arrival — it is the part the reader is done with. ECharts reads this from the
// option already in place when an update begins, not from the one that starts it, which is why the
// value lives here rather than being passed with the rows-out draw (HorizontalBarChart's
// ROWS_OUT_MS has to match it).
const ROWS_OUT_MS = 150;
// The closest built-in to ease-standard's cubic-bezier(0.4, 0, 0.2, 1).
const NAVIGATION_EASING = "cubicInOut";

/**
 * Series-level animation keys. A navigation is two movements, and ECharts times them separately:
 * `animationDurationUpdate` carries the rows being replaced back to the axis, `animationDuration`
 * brings the new ones in. Left at their defaults those ran 400ms and 1000ms, which is the same two
 * phases in the wrong proportion — the exit dragging and the entrance outlasting it.
 *
 * `reducedMotion` turns animation off outright: a reader who asked for less motion does not want
 * rows sliding around, and ECharts has no switch that respects the OS setting on its own.
 */
export function seriesMotion(
  settings: MotionSettings,
  { reducedMotion }: { reducedMotion: boolean }
): {
  animation?: boolean;
  animationDuration?: number;
  animationDurationUpdate?: number;
  animationEasing?: string;
  animationEasingUpdate?: string;
} {
  if (settings.motion !== "navigation") return {};
  if (reducedMotion) return { animation: false };

  return {
    animationDuration: ROWS_IN_MS,
    animationDurationUpdate: ROWS_OUT_MS,
    animationEasing: NAVIGATION_EASING,
    animationEasingUpdate: NAVIGATION_EASING,
  };
}

/** True when the reader has asked for less motion. Kept here so callers share one spelling of it. */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}
